import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import PropTypes from "prop-types";
import "./BetSelector.css";
import CryptoIconWrapper from "./cryptoicons/CryptoIconWrapper";

const BetSelector = ({ onClose, raceData }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [poolData, setPoolData] = useState(raceData.poolData);
  const [selectedTokens, setSelectedTokens] = useState({});
  const [bets, setBets] = useState({});
  const [totalPayout, setTotalPayout] = useState(0);

  // Handle escape key press
  const handleEscapeKey = useCallback((event) => {
    if (event.key === "Escape") {
      handleClose();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [handleEscapeKey]);

  // Initialize selected tokens and bets
  useEffect(() => {
    if (raceData.poolData) {
      setSelectedTokens(
        raceData.poolData.reduce(
          (acc, token) => ({ ...acc, [token.symbol]: false }),
          {}
        )
      );
      setBets(
        raceData.poolData.reduce(
          (acc, token) => ({ ...acc, [token.symbol]: "" }),
          {}
        )
      );
    }
  }, [raceData]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleTokenSelect = (symbol) => {
    setSelectedTokens((prev) => ({
      ...prev,
      [symbol]: !prev[symbol],
    }));
  };

  const calculateUpdatedOdds = (currentPoolData, newBets) => {
    const totalPoolSize = currentPoolData.reduce(
      (sum, token) => sum + token.poolSize,
      0
    );

    return currentPoolData.map((token) => {
      const currentBet = parseFloat(newBets[token.symbol]) || 0;
      if (currentBet === 0) {
        return token;
      }

      const adjustedPoolSize = token.poolSize + currentBet;
      const odds = (totalPoolSize + currentBet) / adjustedPoolSize;

      return {
        ...token,
        odds: parseFloat(odds.toFixed(2)),
      };
    });
  };

  const handleBetChange = (symbol, value) => {
    const newBets = {
      ...bets,
      [symbol]: value,
    };
    setBets(newBets);

    // Calculate new odds
    const updatedData = calculateUpdatedOdds(poolData, newBets);
    setPoolData(updatedData);

    // Calculate total potential payout
    const total = Object.entries(newBets).reduce((sum, [sym, bet]) => {
      if (bet && selectedTokens[sym]) {
        const token = updatedData.find((t) => t.symbol === sym);
        return sum + (parseFloat(bet) || 0) * (token?.odds || 0);
      }
      return sum;
    }, 0);
    setTotalPayout(total);
  };

  const handlePlaceBets = () => {
    // Here you would implement the actual bet placement logic
    // For now, just close the modal
    handleClose();
  };

  return (
    <div
      className={`modal-overlay ${isClosing ? "closing" : ""}`}
      onClick={handleOverlayClick}
    >
      <div className="modal-content">
        <div className="bet-selector">
          <div className="bet-selector-header">
            <button className="back-button" onClick={handleClose}>
              <ArrowLeft size={20} />
              Back
            </button>
          </div>

          <div className="bet-selector-title">
            <h1>Bet Selector</h1>
          </div>

          <div className="header">
            <h2>
              {raceData.title} #{raceData.id}
            </h2>
            <div className="status-bar">
              <div className="status-pill"></div>
              <p className="description">
                Select tokens and place your bets. Odds update in real-time
                based on pool activity.
              </p>
            </div>
          </div>

          <div className="token-selection">
            {poolData.map((token) => (
              <div
                key={token.symbol}
                className={`token-option ${selectedTokens[token.symbol] ? "selected" : ""
                  }`}
                onClick={() => handleTokenSelect(token.symbol)}
              >
                <div className="token-odds">
                  {(token.odds || 0).toFixed(2)}x
                </div>
                <div className="token-icon-wrapper">
                  <CryptoIconWrapper
                    name={token.symbol.toLowerCase()}
                    size={40}
                    className="token-icon"
                  />
                </div>
                <div className="token-details">
                  <div className="token-symbol">{token.symbol}</div>
                  <div className="token-pool">
                    ${(token.poolSize || 0).toLocaleString()}
                  </div>
                </div>
                <label className="toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={selectedTokens[token.symbol] || false}
                    onChange={() => handleTokenSelect(token.symbol)}
                  />
                  <span className="toggle-slider">
                    <span className="toggle-button"></span>
                  </span>
                </label>
              </div>
            ))}
          </div>

          {Object.values(selectedTokens).some((v) => v) && (
            <div className="betting-section">
              <h2>Place Your Bets</h2>
              <div className="odds-info">
                <p>
                  Watch the odds update instantly as you place your bets. Higher
                  bets lower potential returns, while smaller bets keep them
                  higher.
                </p>
              </div>

              <div className="bet-list">
                {Object.entries(selectedTokens).map(
                  ([symbol, isSelected]) =>
                    isSelected && (
                      <div key={symbol} className="bet-item">
                        <div className="bet-token-info">
                          <CryptoIconWrapper
                            name={symbol.toLowerCase()}
                            size={32}
                            className="bet-token-icon"
                          />
                          <div className="bet-token-details">
                            <span className="bet-token-symbol">{symbol}</span>
                            <span className="bet-token-odds">
                              Current Odds:{" "}
                              {(
                                poolData.find((t) => t.symbol === symbol)
                                  ?.odds || 0
                              ).toFixed(2)}
                              x
                            </span>
                          </div>
                        </div>

                        <div className="bet-amount-section">
                          <div className="bet-input-container">
                            <span className="currency-symbol">$</span>
                            <input
                              type="number"
                              value={bets[symbol] || ""}
                              onChange={(e) =>
                                handleBetChange(symbol, e.target.value)
                              }
                              placeholder="0.00"
                              className="bet-amount-input"
                            />
                          </div>
                          <div className="potential-payout">
                            Potential Payout $
                            {(
                              (parseFloat(bets[symbol]) || 0) *
                              (poolData.find((t) => t.symbol === symbol)
                                ?.odds || 0)
                            ).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    )
                )}
              </div>

              <div className="bet-summary">
                <div className="total-payout">
                  <span>Total Potential Payout</span>
                  <span className="total-amount">
                    ${totalPayout.toFixed(2)}
                  </span>
                </div>
                <button
                  className="place-bets-button"
                  onClick={handlePlaceBets}
                  disabled={
                    !Object.values(bets).some((bet) => parseFloat(bet) > 0)
                  }
                >
                  Place Bets
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

BetSelector.propTypes = {
  onClose: PropTypes.func.isRequired,
  raceData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    poolData: PropTypes.arrayOf(
      PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        odds: PropTypes.number.isRequired,
        poolSize: PropTypes.number.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default BetSelector;
